import type { TUnbxdProduct } from 'actions/search';
import type { LocaleObject } from 'hooks';
import { FAMILIES_UNBXD } from './constants/products';
import { getDomainValue } from './domain';
import { getLang, getMarket, getRealLanguage } from './helpers';
import { DOCTYPES } from './constants/search';

export function getUnbxdTarget(product: TUnbxdProduct) {
  const TARGETS = { WOMEN: 'W', MEN: 'M', KIDS: 'K', GOODS: 'G', UNISEX: 'X', ALL: 'A' };

  if (product?.goods?.toLowerCase() === 'yes') {
    return TARGETS.GOODS;
  }
  if (product?.age_group?.toLowerCase() === 'kids') {
    return TARGETS.KIDS;
  }
  switch (product.gender?.[0]?.toLowerCase()) {
    case 'women':
      return TARGETS.WOMEN;
    case 'unisex':
      return TARGETS.UNISEX;
    default:
      return TARGETS.MEN;
  }
}

// if suffix is ${letter}F, return ${letter} if it is F or ${letter} return original
function getSuffix(suffix: string) {
  if (suffix?.length > 1 && suffix?.[suffix.length - 1] === 'F') {
    return suffix?.[0];
  }
  return suffix;
}

export function getImagesSrc({
  sku,
  suffix,
  newShooting,
  square = false,
}: {
  sku: string;
  name: string;
  suffix: string;
  newShooting: boolean;
  square?: boolean;
}) {
  const suffixFormatted = getDomainValue({
    camper: square ? getSuffix(suffix) : suffix || 'LF',
    nnormal: square ? getSuffix(suffix) : suffix || 'LF',
    camperlab: getSuffix(suffix) || 'LF',
  });

  const urlBase = `https://cloud.${getDomainValue({ camper: 'camper', nnormal: 'nnormal', camperlab: 'camperlab' })}.com/is/image/{recorte}/${sku}_${suffixFormatted}.jpg`;
  const images = {
    xs: urlBase.replace(
      '{recorte}',
      newShooting ?
        getDomainValue({
          camper: square ? 'JGVzaG9wMDNtZWRpdW13aGl0ZSQ=' : 'JGVzaG9wMDFwZHBzcXVhcmVncmV5JA==',
          nnormal: 'camper',
          camperlab: square ? 'JGVzaG9wMDNtZWRpdW13aGl0ZSQ=' : 'JGNhbXBlcmxhYjAxc21hbGwk',
        })
      : getDomainValue({
          camper: square ? 'JGVzaG9wMDNtZWRpdW13aGl0ZSQ=' : 'JGVzaG9wMDNuc21hbGxncmV5JA==',
          camperlab: square ? 'JGVzaG9wMDNtZWRpdW13aGl0ZSQ=' : 'JGNhbXBlcmxhYjAxc21hbGwk',
          nnormal: 'JGxvY2F0b3IzJA==',
        }),
    ),
    md: urlBase.replace(
      '{recorte}',
      newShooting ?
        getDomainValue({
          camper: square ? 'JGVzaG9wMDNtZWRpdW13aGl0ZSQ=' : 'JGVzaG9wMDFwZHBzcXVhcmVncmV5bSQ=',
          nnormal: 'camper',
          camperlab: square ? 'JGVzaG9wMDNtZWRpdW13aGl0ZSQ=' : 'JGNhbXBlcmxhYjAxbWVkaXVtJA==',
        })
      : getDomainValue({
          camper: square ? 'JGVzaG9wMDNtZWRpdW13aGl0ZSQ=' : 'JGVzaG9wMDNubWVkaXVtZ3JleSQ=',
          camperlab: square ? 'JGVzaG9wMDNtZWRpdW13aGl0ZSQ=' : 'JGNhbXBlcmxhYjAxbWVkaXVtJA==',
          nnormal: 'JGxvY2F0b3IzJA==',
        }),
    ),
    lg: urlBase.replace(
      '{recorte}',
      newShooting ?
        getDomainValue({
          camper: 'JGVzaG9wMDFwZHBzcXVhcmVncmV5dCQ=',
          nnormal: 'camper',
          camperlab: square ? 'Y2FtcGVybGFiYmctMXgxLTgwMA==' : 'JGNhbXBlcmxhYjAzbmIk',
        })
      : getDomainValue({
          camper: 'JGVzaG9wMDNuYmlnZ3JleSQ=',
          camperlab: square ? 'Y2FtcGVybGFiYmctMXgxLTgwMA==' : 'JGNhbXBlcmxhYjAzbmIk',
          nnormal: 'JGVzaG9wMDNtYmlnZ3JleSQ=',
        }),
    ),
    full: urlBase.replace(
      '{recorte}',
      newShooting ?
        getDomainValue({
          camper: 'JGVzaG9wMDFwZHBzcXVhcmVncmV5JA==',
          nnormal: 'camper',
          camperlab: square ? 'Y2FtcGVybGFiYmctMXgxLTgwMA==' : 'JGNhbXBlcmxhYjAzem8k',
        })
      : getDomainValue({
          camper: 'JGVzaG9wMDNudmJpZ2dyZXkk',
          camperlab: square ? 'Y2FtcGVybGFiYmctMXgxLTgwMA==' : 'JGNhbXBlcmxhYjAzem8k',
          nnormal: 'YnJldW5pbmdlcjAx',
        }),
    ),
  };
  return images;
}

export function getProductsUnbxdFormatted(responseProducts: TUnbxdProduct[], locale: string) {
  const lang = getLang(locale);
  const realLang = getRealLanguage(lang);
  const market = getMarket(locale);
  const isoLanguage = `${realLang}-${market}`;

  const responseProductsFormatted = responseProducts?.map((product) => {
    const gender = getUnbxdTarget(product);
    const currency = product[`${market}_currency_unx_ts`];
    const hasDiscount = product[`${market}_is_on_sale_unx_ts`]?.toLowerCase() === 'true';
    const salePercentage = product[`${market}_sale_percentage`] || null;
    const maxPriceEur = product[`${market}_max_price_EUR_unx_d`];
    const minPriceEur = product[`${market}_min_price_EUR_unx_d`];
    const discount =
      hasDiscount ?
        Number(salePercentage) ||
        Math.ceil(((product[`${market}_max_price_unx_d`] - product[`${market}_max_sale_price_unx_d`]) / product[`${market}_max_price_unx_d`]) * 100)
      : null;
    const hasRangePrice = maxPriceEur !== minPriceEur;
    const rangePrices: {
      priceFrom?: number;
      priceFromFormatted?: string;
      priceTo?: number;
      priceToFormatted?: string;
      previousFromFormatted?: string;
      previousToFormatted?: string;
      minPriceUnbx?: string;
      maxPriceUnbx?: string;
    } = {};
    let usePreviousMinAndMaxPrice = false;
    if (hasRangePrice) {
      const maxSalePrice = product[`${market}_max_sale_price_unx_d`] || product[`${market}_max_price_unx_d`];
      const minSalePrice = product[`${market}_min_sale_price_unx_d`] || product[`${market}_min_price_unx_d`];
      rangePrices.priceFrom = minSalePrice;
      rangePrices.priceFromFormatted = minSalePrice?.toLocaleString(isoLanguage, { style: 'currency', currency: currency || 'EUR' });
      rangePrices.priceTo = maxSalePrice;
      rangePrices.priceToFormatted = maxSalePrice?.toLocaleString(isoLanguage, { style: 'currency', currency: currency || 'EUR' });
      if (product[`${market}_min_sale_price_unx_d`] && product[`${market}_max_sale_price_unx_d`]) {
        rangePrices.previousFromFormatted = product[`${market}_min_price_unx_d`].toLocaleString(isoLanguage, {
          style: 'currency',
          currency: currency || 'EUR',
        });
        rangePrices.previousToFormatted = product[`${market}_max_price_unx_d`].toLocaleString(isoLanguage, { style: 'currency', currency: currency || 'EUR' });
        rangePrices.minPriceUnbx = rangePrices.previousFromFormatted;
        rangePrices.maxPriceUnbx = rangePrices.previousToFormatted;
        usePreviousMinAndMaxPrice = true;
      }
    }
    const current = hasDiscount ? Number(product[`${market}_max_sale_price_unx_d`]) : product[`${market}_max_price_unx_d`];
    const currentFormated =
      hasDiscount ?
        current?.toLocaleString(isoLanguage, { style: 'currency', currency: currency || 'EUR' })
      : current?.toLocaleString(isoLanguage, { style: 'currency', currency: currency || 'EUR' });
    const previous = hasDiscount ? product[`${market}_max_price_unx_d`] : null;
    const previousFormated = hasDiscount ? previous?.toLocaleString(isoLanguage, { style: 'currency', currency: currency || 'EUR' }) : null;
    const discountPrices: { discount?: number; discountEur?: number; previous?: number; previousFormated?: string } = {};
    if (hasDiscount) {
      discountPrices.discount = discount;
      discountPrices.discountEur = product[`${market}_max_sale_price_EUR_unx_d`] || 0;
      discountPrices.previous = previous;
      discountPrices.previousFormated = previousFormated;
    }
    const voucherPrelaunch = product[`${market}_voucher_prelaunch`] === 'true';
    const voucherType = product[`${market}_voucher_type`] || null;
    const voucherCode = product[`${market}_voucher_code`] || null;
    const voucherDiscount = product[`${market}_voucher_discount_unx_ts`] || null;
    const voucherMaxVoucherPrice = product[`${market}_max_voucher_price_unx_ts`] || null;
    const voucherMinVoucherPrice = product[`${market}_min_voucher_price_unx_ts`] || null;
    const adqCampaign = product[`${market}_voucher_campaign_type`] === 'adquisition';
    const usrHash = product[`${market}_voucher_autoapply`] === 'true';
    let voucherPrices = null;
    if (voucherCode) {
      const voucherHasPricePerSize = voucherMaxVoucherPrice !== voucherMinVoucherPrice;
      const voucherRangePrices: { priceFrom?: number; priceFromFormatted?: string; priceTo?: number; priceToFormatted?: string } = {};
      if (voucherHasPricePerSize) {
        voucherRangePrices.priceFrom = voucherMinVoucherPrice;
        voucherRangePrices.priceFromFormatted = Number(voucherMinVoucherPrice)?.toLocaleString(isoLanguage, { style: 'currency', currency: currency || 'EUR' });
        voucherRangePrices.priceTo = voucherMaxVoucherPrice;
        voucherRangePrices.priceToFormatted = Number(voucherMaxVoucherPrice)?.toLocaleString(isoLanguage, { style: 'currency', currency: currency || 'EUR' });
      }
      const previousPrice =
        product[`${market}_max_price_unx_d`] > Number(voucherMaxVoucherPrice) ? product[`${market}_max_price_unx_d`] : Number(voucherMaxVoucherPrice);
      voucherPrices = {
        [voucherCode]: {
          current: voucherMaxVoucherPrice,
          currency,
          previous: previousPrice,
          discount: voucherDiscount,
          discountStr: `${voucherDiscount}%`,
          pricePerSize: voucherHasPricePerSize,
          voucherRangePrices,
          currentFormated: Number(voucherMaxVoucherPrice)?.toLocaleString(isoLanguage, { style: 'currency', currency: currency || 'EUR' }),
          previousFormated: Number(previousPrice)?.toLocaleString(isoLanguage, { style: 'currency', currency: currency || 'EUR' }),
          preLaunch: voucherPrelaunch,
          voucherType,
          voucherColIds: [
            // Falseada vacia. Esto se usa para ver si la grid en que estás es la correcta. Pero en el buscador nunca lo es
          ],
          adqCampaign,
          usrHash,
        },
      };
    }
    return {
      category: product.catalog || '',
      color: product?.color?.length ? product.color[0].toLowerCase() : '',
      family: Object.keys(FAMILIES_UNBXD).includes(product.catalog) ? FAMILIES_UNBXD[product.catalog] : '',
      id: product?.uniqueId || '',
      images: getImagesSrc({
        sku: product?.uniqueId,
        name: product?.title,
        suffix: product?.grid_image_suffix || 'LF',
        newShooting: getDomainValue({ camper: product?.is_shooting_new === 'true', nnormal: false, camperlab: product?.is_shooting_new === 'true' }),
      }),
      name: product.title || '',
      nameBase: product.title || '', // de momento usamos el mismo que name
      newShooting: product?.is_shooting_new === 'true' || false,
      prices: {
        currency: currency || 'EUR',
        current,
        currentEur: product[`${market}_max_price_EUR_unx_d`] || 0,
        currentFormated,
        ...discountPrices,
        pricePerSize: hasRangePrice,
        ...rangePrices,
        usePreviousMinAndMaxPrice,
      },
      targetId: gender,
      url: product[`${market}_${realLang}_productUrl`] || '',
      voucherPrices: voucherPrices || null,
      target: gender,
    };
  });

  return responseProductsFormatted;
}

const baseUrl = process.env.INTEGRA_DOMAIN;

function getSearchProductUrl(url?: string) {
  try {
    if (!url || typeof url !== 'string') {
      return '';
    }
    const base = typeof window !== 'undefined' ? window.location.origin : baseUrl;
    const existingUrl = new URL(url, base);
    const newUrl = new URL(existingUrl.pathname, base);
    return newUrl.toString();
  } catch (error) {
    console.error('Error creating URL:', error);
    return url;
  }
}

export function normalizeUnbxdProducts(
  responseProducts: TUnbxdProduct[],
  {
    realLanguage,
    country,
    realLocale,
    filter = false,
    square = false,
  }: Pick<LocaleObject, 'realLanguage' | 'country' | 'realLocale'> & { filter?: boolean; square?: boolean },
) {
  // TODO: cambiar a reduce en vez de filter y map cuando tipemos el responseProducts
  const responseProductsFormatted =
    responseProducts
      ?.filter((product) => {
        if (filter === false) return true;
        return product.doctype === DOCTYPES.POPULAR_PRODUCTS;
      })
      ?.map((product, index) => {
        const description = product[`${realLanguage}_product_sheet_h1`];
        const gender = getUnbxdTarget(product);
        const currency = product[`${country}_currency_unx_ts`];
        const hasDiscount = product[`${country}_is_on_sale_unx_ts`]?.toLowerCase() === 'true';
        const salePercentage = product[`${country}_sale_percentage`] || null;
        const maxPriceEur = product[`${country}_max_price_EUR_unx_d`];
        const minPriceEur = product[`${country}_min_price_EUR_unx_d`];
        const discount =
          hasDiscount ?
            Number(salePercentage) ||
            Math.ceil(((product[`${country}_max_price_unx_d`] - product[`${country}_max_sale_price_unx_d`]) / product[`${country}_max_price_unx_d`]) * 100)
          : null;
        const hasRangePrice = maxPriceEur !== minPriceEur;
        const rangePrices: {
          priceFrom?: number;
          priceFromFormatted?: string;
          priceTo?: number;
          priceToFormatted?: string;
          previousFromFormatted?: string;
          previousToFormatted?: string;
          minPriceUnbx?: string;
          maxPriceUnbx?: string;
        } = {};
        let usePreviousMinAndMaxPrice = false;
        if (hasRangePrice) {
          const maxSalePrice = product[`${country}_max_sale_price_unx_d`] || product[`${country}_max_price_unx_d`];
          const minSalePrice = product[`${country}_min_sale_price_unx_d`] || product[`${country}_min_price_unx_d`];
          rangePrices.priceFrom = minSalePrice;
          rangePrices.priceFromFormatted = minSalePrice?.toLocaleString(realLocale, { style: 'currency', currency: currency || 'EUR' });
          rangePrices.priceTo = maxSalePrice;
          rangePrices.priceToFormatted = maxSalePrice?.toLocaleString(realLocale, { style: 'currency', currency: currency || 'EUR' });
          if (product[`${country}_min_sale_price_unx_d`] && product[`${country}_max_sale_price_unx_d`]) {
            rangePrices.previousFromFormatted = product[`${country}_min_price_unx_d`].toLocaleString(realLocale, {
              style: 'currency',
              currency: currency || 'EUR',
            });
            rangePrices.previousToFormatted = product[`${country}_max_price_unx_d`].toLocaleString(realLocale, {
              style: 'currency',
              currency: currency || 'EUR',
            });
            rangePrices.minPriceUnbx = rangePrices.previousFromFormatted;
            rangePrices.maxPriceUnbx = rangePrices.previousToFormatted;
            usePreviousMinAndMaxPrice = true;
          }
        }
        const current = hasDiscount ? Number(product[`${country}_max_sale_price_unx_d`]) : Number(product[`${country}_max_price_unx_d`]);
        const currentFormated = current.toLocaleString(realLocale, { style: 'currency', currency: currency || 'EUR' });
        const previous = hasDiscount ? product[`${country}_max_price_unx_d`] : null;
        const previousFormated = hasDiscount ? previous?.toLocaleString(realLocale, { style: 'currency', currency: currency || 'EUR' }) : null;
        const discountPrices: { discount?: number; discountEur?: number; previous?: number; previousFormated?: string } = {};
        if (hasDiscount) {
          discountPrices.discount = discount;
          discountPrices.discountEur = product[`${country}_max_sale_price_EUR_unx_d`] || 0;
          discountPrices.previous = previous;
          discountPrices.previousFormated = previousFormated;
        }
        const voucherPrelaunch = product[`${country}_voucher_prelaunch`] === 'true';
        const voucherType = product[`${country}_voucher_type`] || null;
        const voucherCode = product[`${country}_voucher_code`] || null;
        const voucherDiscount = product[`${country}_voucher_discount_unx_ts`] || null;
        const voucherMaxVoucherPrice = product[`${country}_max_voucher_price_unx_ts`] || null;
        const voucherMinVoucherPrice = product[`${country}_min_voucher_price_unx_ts`] || null;
        const adqCampaign = product[`${country}_voucher_campaign_type`] === 'adquisition';
        const usrHash = product[`${country}_voucher_autoapply`] === 'true';
        let voucherPrices = null;
        if (voucherCode) {
          const voucherHasPricePerSize = voucherMaxVoucherPrice !== voucherMinVoucherPrice;
          const voucherRangePrices: { priceFrom?: number; priceFromFormatted?: string; priceTo?: number; priceToFormatted?: string } = {};
          if (voucherHasPricePerSize) {
            voucherRangePrices.priceFrom = voucherMinVoucherPrice;
            voucherRangePrices.priceFromFormatted = Number(voucherMinVoucherPrice)?.toLocaleString(realLocale, {
              style: 'currency',
              currency: currency || 'EUR',
            });
            voucherRangePrices.priceTo = voucherMaxVoucherPrice;
            voucherRangePrices.priceToFormatted = Number(voucherMaxVoucherPrice)?.toLocaleString(realLocale, {
              style: 'currency',
              currency: currency || 'EUR',
            });
          }
          const previousPrice =
            product[`${country}_max_price_unx_d`] > Number(voucherMaxVoucherPrice) ? product[`${country}_max_price_unx_d`] : Number(voucherMaxVoucherPrice);
          voucherPrices = {
            [voucherCode]: {
              current: voucherMaxVoucherPrice,
              currency,
              previous: previousPrice,
              discount: voucherDiscount,
              discountStr: `${voucherDiscount}%`,
              pricePerSize: voucherHasPricePerSize,
              voucherRangePrices,
              currentFormated: Number(voucherMaxVoucherPrice)?.toLocaleString(realLocale, { style: 'currency', currency: currency || 'EUR' }),
              previousFormated: Number(previousPrice)?.toLocaleString(realLocale, { style: 'currency', currency: currency || 'EUR' }),
              preLaunch: voucherPrelaunch,
              voucherType,
              voucherColIds: [
                // Falseada vacia. Esto se usa para ver si la grid en que estás es la correcta. Pero en el buscador nunca lo es
              ],
              adqCampaign,
              usrHash,
            },
          };
        }

        return {
          category: product.catalog || '',
          color: product?.color?.length ? product.color[0].toLowerCase() : '',
          family: Object.keys(FAMILIES_UNBXD).includes(product.catalog) ? FAMILIES_UNBXD[product.catalog] : '',
          description: description || '',
          id: product?.uniqueId || '',
          images: getImagesSrc({
            sku: product?.uniqueId,
            name: product?.title,
            suffix: product?.grid_image_suffix || 'LF',
            newShooting: getDomainValue({
              camper: product?.is_shooting_new === 'true' || false,
              camperlab: product?.is_shooting_new === 'true' || false,
              nnormal: false,
            }),
            square,
          }),
          name: product.title || '',
          nameBase: product.title || '', // de momento usamos el mismo que name
          newShooting: product?.is_shooting_new === 'true' || false,
          prices: {
            currency: currency || 'EUR',
            current,
            currentEur: product[`${country}_max_price_EUR_unx_d`] || 0,
            currentFormated,
            ...discountPrices,
            pricePerSize: hasRangePrice,
            ...rangePrices,
            usePreviousMinAndMaxPrice,
          },
          targetId: gender,
          url: getSearchProductUrl(product[`${country}_${realLanguage}_productUrl`]),
          voucherPrices: voucherPrices || null,
          target: gender,
          autosuggest: product.autosuggest || '',
          doctype: product.doctype || '',
          uniqueId: product.uniqueId || '',
          imageUrl: product.imageUrl || [],
          title: product.title || '',
        };
      }) ?? [];

  return responseProductsFormatted;
}

export type TUnbxdProductNormalized = ReturnType<typeof normalizeUnbxdProducts>[number];
